
// ─── src/components/dashboard/ApplicantsTable.tsx ─────────────────────────────
import { useState, type FC } from "react";
import { ExternalLink, Eye, Lightbulb, Award, CalendarCheck, CheckCircle, XCircle, Pencil, Trash2 } from "lucide-react";
import type { Registration } from "../../types/registration";
import { avatarColor, initials, capitalize } from "../../utils/formatter";
import { ApplicantDetailModal } from "./ApplicantDetailModal";
import { EditApplicantModal } from "./EditApplicantModal";
import { useUpdateAcceptance, useDeleteRegistration } from "../../hooks/useRegistrations";

interface Props {
  registrations: Registration[];
  isMaster?: boolean;
}

const COL_HEADERS = [
  "Applicant", "City", "Role", "Experience",
  "App. Status", "Flags", "Actions",
];

const APP_STATUS_BADGE: Record<string, string> = {
  pending:  "bg-amber-500/20 text-amber-300 border border-amber-500/30",
  accepted: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  rejected: "bg-red-500/20 text-red-300 border border-red-500/30",
};

export const ApplicantsTable: FC<Props> = ({ registrations, isMaster = false }) => {
  const [selected,   setSelected]   = useState<Registration | null>(null);
  const [editTarget, setEditTarget] = useState<Registration | null>(null);
  const { mutate: updateAcceptance, isPending, variables } = useUpdateAcceptance();
  const { mutate: deleteReg, isPending: isDeleting, variables: deleteVars } = useDeleteRegistration();

  const handleDelete = (reg: Registration) => {
    if (!window.confirm(`Delete "${reg.fullName}"? This cannot be undone.`)) return;
    deleteReg(reg.id);
  };

  return (
    <>
      <div className="overflow-x-auto rounded-2xl border border-brand-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border bg-brand-border/30">
              {COL_HEADERS.map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold
                             text-brand-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {registrations.map((reg, i) => (
              <ApplicantRow
                key={reg.id ?? i}
                reg={reg}
                isMaster={isMaster}
                isRowPending={isPending && variables?.id === reg.id}
                isDeletePending={isDeleting && deleteVars === reg.id}
                onView={() => setSelected(reg)}
                onAccept={() => updateAcceptance({ id: reg.id, accepted: true })}
                onReject={() => updateAcceptance({ id: reg.id, accepted: false })}
                onEdit={() => setEditTarget(reg)}
                onDelete={() => handleDelete(reg)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <ApplicantDetailModal
          reg={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {editTarget && (
        <EditApplicantModal
          reg={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
    </>
  );
};

// ── Row ────────────────────────────────────────────────────────────────────────
const ApplicantRow: FC<{
  reg:             Registration;
  isMaster:        boolean;
  isRowPending:    boolean;
  isDeletePending: boolean;
  onView:          () => void;
  onAccept:        () => void;
  onReject:        () => void;
  onEdit:          () => void;
  onDelete:        () => void;
}> = ({ reg, isMaster, isRowPending, isDeletePending, onView, onAccept, onReject, onEdit, onDelete }) => (
  <tr
    className="border-b border-brand-border/50 hover:bg-brand-border/20
               transition-colors group"
  >
    {/* Applicant */}
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div
          className={`${avatarColor(reg.fullName)} w-9 h-9 rounded-full
                      flex items-center justify-center text-white text-xs font-bold shrink-0`}
        >
          {initials(reg.fullName)}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-white truncate max-w-[160px]">
            {reg.fullName}
          </p>
          <p className="text-brand-muted text-xs truncate max-w-[160px]">
            {reg.email}
          </p>
        </div>
      </div>
    </td>

    {/* City */}
    <td className="px-4 py-3 text-brand-muted">{reg.city}</td>

    {/* Role */}
    <td className="px-4 py-3 text-white capitalize">{reg.primaryRole}</td>

    {/* Experience */}
    <td className="px-4 py-3 text-brand-muted text-center">
      {reg.yearsOfExperience} yr{reg.yearsOfExperience !== 1 ? "s" : ""}
    </td>

    {/* App. Status */}
    <td className="px-4 py-3">
      <span
        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                    ${APP_STATUS_BADGE[reg.status ?? ""] ?? "bg-gray-500/20 text-gray-300 border border-gray-500/30"}`}
      >
        {reg.status ?? "—"}
      </span>
    </td>

    {/* Flags */}
    <td className="px-4 py-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        {reg.isPitchingIdea && (
          <span
            title="Pitching an idea"
            className="flex items-center gap-1 px-2 py-0.5 rounded-full
                       bg-brand-orange/20 text-brand-orange border
                       border-brand-orange/30 text-xs"
          >
            <Lightbulb size={10} /> Idea
          </span>
        )}
        {reg.requestingScholarship && (
          <span
            title="Requesting scholarship"
            className="flex items-center gap-1 px-2 py-0.5 rounded-full
                       bg-blue-500/20 text-blue-300 border
                       border-blue-500/30 text-xs"
          >
            <Award size={10} /> Scholarship
          </span>
        )}
        {reg.availableAllDays && (
          <span
            title="Available all days"
            className="flex items-center gap-1 px-2 py-0.5 rounded-full
                       bg-emerald-500/20 text-emerald-300 border
                       border-emerald-500/30 text-xs"
          >
            <CalendarCheck size={10} /> All Days
          </span>
        )}
        {!reg.isPitchingIdea && !reg.requestingScholarship && !reg.availableAllDays && (
          <span className="text-brand-muted text-xs">—</span>
        )}
      </div>
    </td>

    {/* Actions */}
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <button
          onClick={onView}
          title="View details"
          className="p-1.5 rounded-lg text-brand-muted hover:text-white
                     hover:bg-brand-border transition-colors"
        >
          <Eye size={15} />
        </button>
        {reg.linkedInUrl && (
          <a
            href={reg.linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="LinkedIn"
            className="p-1.5 rounded-lg text-brand-muted hover:text-brand-orange
                       hover:bg-brand-border transition-colors"
          >
            <ExternalLink size={15} />
          </a>
        )}
        {isMaster && (!reg.status || reg.status === "pending") && (
          <>
            <button
              onClick={onAccept}
              disabled={isRowPending}
              title="Accept applicant"
              className="p-1.5 rounded-lg text-brand-muted hover:text-emerald-400
                         hover:bg-emerald-500/10 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <CheckCircle size={15} />
            </button>
            <button
              onClick={onReject}
              disabled={isRowPending}
              title="Reject applicant"
              className="p-1.5 rounded-lg text-brand-muted hover:text-red-400
                         hover:bg-red-500/10 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <XCircle size={15} />
            </button>
          </>
        )}
        {isMaster && (
          <>
            <button
              onClick={onEdit}
              title="Edit applicant"
              className="p-1.5 rounded-lg text-brand-muted hover:text-blue-400
                         hover:bg-blue-500/10 transition-colors"
            >
              <Pencil size={15} />
            </button>
            <button
              onClick={onDelete}
              disabled={isDeletePending}
              title="Delete applicant"
              className="p-1.5 rounded-lg text-brand-muted hover:text-red-400
                         hover:bg-red-500/10 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 size={15} />
            </button>
          </>
        )}
      </div>
    </td>
  </tr>
);
